import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {iif, of, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, switchMap, take} from 'rxjs/operators';
import {ComuneModel, DatiAnagPersonaFisicaModel, LocalitaModel, NazioneModel} from '../../models/estremi-persona-fisica.model';
import {getDateErrorMessage, getErrorMessage} from '../../utils/validators-messages.utils';
import {ValidateCodiceFiscalePIVA, ValidateName} from "../../utils/validators.utils";
import {ModalitaPagamentoService} from '../../services/modalita-pagamento.service';
@Component({
	selector: 'lib-estremi-persona-fisica',
	templateUrl: './estremi-persona-fisica.component.html',
	styleUrls: ['./estremi-persona-fisica.component.scss']
})
export class EstremiPersonaFisicaComponent implements OnInit, OnDestroy {

	@Input()
	ngxsFormPath: string;

	@Input()
	province: LocalitaModel[];

	@Input()
	nazioni: NazioneModel[];

	@Input()
	isPersonaFisicaValid: boolean;

	@Input()
	personaFisica: DatiAnagPersonaFisicaModel;

	@Input()
	viewSessoPF: boolean = false;

	@Output()
	validatePersonaFisicaEmitter = new EventEmitter<void>();

	@Output()
	personaFisicaEmitter = new EventEmitter<{ persona: DatiAnagPersonaFisicaModel, isValid: boolean }>();

	@Input('disableForm')
	set _disableForm(val: boolean) {
		this.disableForm = val;
		if (this.disableForm) {
			this.estremiPersonaFisicaForm.disable();
			this.validatePersonaFisicaEmitter.emit();
		} else {
			this.estremiPersonaFisicaForm.enable();
		}
	}

	disableForm: boolean;

	comuni: ComuneModel[];

	estremiPersonaFisicaForm: FormGroup;
	maxDataNascita = new Date();

	private subscription: Subscription = new Subscription();

	getErrorMessage = getErrorMessage;
	getDateErrorMessage = getDateErrorMessage;

	constructor(private modalitaPagamentoService: ModalitaPagamentoService) {
		// this.initPersonaFisicaForm();
		// this.setCodiceFiscaleValidators();
	}

	ngOnInit(): void {
		this.initPersonaFisicaForm();
		this.setFormControlsValue();
		this.setCodiceFiscaleValidators();

		// Set/Unset form controls validation on isNatoEstero Value Change
		this.subscription.add(this.estremiPersonaFisicaForm.controls['isNatoEstero'].valueChanges.subscribe(isNatoEstero => {
			if (isNatoEstero) {
				this.estremiPersonaFisicaForm.controls['provinciaNascita'].setValidators([])
				this.estremiPersonaFisicaForm.controls['provinciaNascita'].patchValue(null)
				this.estremiPersonaFisicaForm.controls['provinciaNascita'].updateValueAndValidity()
				this.estremiPersonaFisicaForm.controls['codiceComuneNascita'].setValidators([])
				this.estremiPersonaFisicaForm.controls['codiceComuneNascita'].patchValue(null)
				this.estremiPersonaFisicaForm.controls['codiceComuneNascita'].updateValueAndValidity();
				this.estremiPersonaFisicaForm.controls['nazioneNascita'].setValidators([Validators.required])
				this.estremiPersonaFisicaForm.controls['nazioneNascita'].updateValueAndValidity();
			} else {
				this.estremiPersonaFisicaForm.controls['provinciaNascita'].setValidators([Validators.required])
				this.estremiPersonaFisicaForm.controls['provinciaNascita'].updateValueAndValidity()
				this.estremiPersonaFisicaForm.controls['codiceComuneNascita'].setValidators([Validators.required])
				this.estremiPersonaFisicaForm.controls['codiceComuneNascita'].updateValueAndValidity();
				this.estremiPersonaFisicaForm.controls['nazioneNascita'].setValidators([])
				this.estremiPersonaFisicaForm.controls['nazioneNascita'].patchValue(null)
				this.estremiPersonaFisicaForm.controls['nazioneNascita'].updateValueAndValidity();
			}
		}));

		this.getComuniOnProvinciaChange();
		this.validatePersonaFisica();
		this.personaFisicaEmit();
}

	getComuniOnProvinciaChange(): void {
		this.subscription.add(this.estremiPersonaFisicaForm.controls['provinciaNascita'].valueChanges.pipe(
				distinctUntilChanged(),
				switchMap(siglaProvinciaNascita =>
					iif(() => !!siglaProvinciaNascita,
						this.modalitaPagamentoService.getComuni$(siglaProvinciaNascita).pipe(take(1)),
						of([])
					)
				)).subscribe(comuni => {
				this.comuni = comuni;
			})
		);
	}

	validatePersonaFisica(): void {
		this.subscription.add(this.estremiPersonaFisicaForm.valueChanges.pipe(
				debounceTime(500),
				filter(() => this.estremiPersonaFisicaForm.valid),
			).subscribe((_) => {
				this.validatePersonaFisicaEmitter.emit();
			})
		);
	}

	personaFisicaEmit(): void {

		this.subscription.add(this.estremiPersonaFisicaForm.valueChanges.pipe(
				debounceTime(500),
				// filter(() => this.estremiPersonaFisicaForm.valid),
			).subscribe((_) => {
				const personaFisica: DatiAnagPersonaFisicaModel = {
					infoAnagrafiche: {
						codFiscale: this.estremiPersonaFisicaForm.controls['codiceFiscale'].value,
						cognome: this.estremiPersonaFisicaForm.controls['cognome'].value,
						nome: this.estremiPersonaFisicaForm.controls['nome'].value
					},
					infoNascita: {
						comuneNascita : this.estremiPersonaFisicaForm.controls['codiceComuneNascita'].value,
						provinciaNascita : 	this.estremiPersonaFisicaForm.controls['provinciaNascita'].value,
						dataNascita : this.estremiPersonaFisicaForm.controls['dataNascita'].value
					}
				};
				const isValid = this.estremiPersonaFisicaForm.valid;
				this.personaFisicaEmitter.emit({ persona: personaFisica ? personaFisica : null, isValid });
			})
		);
	}

	initPersonaFisicaForm(): void {
		this.estremiPersonaFisicaForm = new FormGroup({
			codiceFiscale: new FormControl(null),
			nome: new FormControl(null, [Validators.required, ValidateName]),
			cognome: new FormControl(null, [Validators.required, ValidateName]),
			isNatoEstero: new FormControl(false),
			provinciaNascita: new FormControl(null),
			codiceComuneNascita: new FormControl(null),
			nazioneNascita: new FormControl(null),
			dataNascita: new FormControl(null, [Validators.required]),
			sesso: new FormControl(null, this.viewSessoPF ? [Validators.required] :  [])
		});
	}

	setCodiceFiscaleValidators() {
		this.estremiPersonaFisicaForm.controls['codiceFiscale'].setValidators([
			Validators.required,
			ValidateCodiceFiscalePIVA
		]);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	convertToUppercase(event: Event) {
		this.estremiPersonaFisicaForm.get('codiceFiscale').setValue((event.target as HTMLInputElement).value.toUpperCase());
	}

	onProvinciaChange(): void {
		this.estremiPersonaFisicaForm.controls['codiceComuneNascita'].patchValue(null);
		this.estremiPersonaFisicaForm.controls['codiceComuneNascita'].markAsTouched();
	}

	setFormControlsValue(): void{
		this.estremiPersonaFisicaForm.controls['codiceFiscale'].setValue(this.personaFisica ? this.personaFisica.infoAnagrafiche.codFiscale : null);
		this.estremiPersonaFisicaForm.controls['nome'].setValue(this.personaFisica ? this.personaFisica.infoAnagrafiche.nome : null);
		this.estremiPersonaFisicaForm.controls['cognome'].setValue(this.personaFisica ? this.personaFisica.infoAnagrafiche.cognome : null);
		this.estremiPersonaFisicaForm.controls['isNatoEstero'].setValue(this.personaFisica && this.personaFisica.infoNascita.comuneNascita.startsWith("Z") ? true : false);
		this.estremiPersonaFisicaForm.controls['provinciaNascita'].setValue(this.personaFisica  && !this.personaFisica.infoNascita.comuneNascita.startsWith("Z") ? this.personaFisica.infoNascita.provinciaNascita : null);
		this.estremiPersonaFisicaForm.controls['codiceComuneNascita'].setValue(this.personaFisica && !this.personaFisica.infoNascita.comuneNascita.startsWith("Z") ? this.personaFisica.infoNascita.comuneNascita : null);
		this.estremiPersonaFisicaForm.controls['dataNascita'].setValue(this.personaFisica ? this.personaFisica.infoNascita.dataNascita : null);
		// TODO
		this.estremiPersonaFisicaForm.controls['sesso'].setValue(this.viewSessoPF ? 'M': null);
		this.estremiPersonaFisicaForm.controls['nazioneNascita'].setValue(this.personaFisica && this.personaFisica.infoNascita.comuneNascita.startsWith("Z") ? this.personaFisica.infoNascita?.comuneNascita : null);
	}
}
